import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * HANDY ROYAL WHEEL (2026-09-20)
 *
 * 그림 그대로 쓴다 — 휠 본체만 돌고, LED 링과 바늘은 고정이다.
 * 결과는 서버가 정한다. 화면은 그 칸이 바늘 밑에 오도록 각도만 맞춘다.
 * 도는 느낌: 천천히 시작 → 빨라짐 → 천천히 멈춤 (한 번의 긴 ease-in-out)
 *
 * 배치 원칙 — 스크롤 없음.
 *   배경(bg.webp)은 cover 로 화면을 꽉 채우고, 모든 요소는 배경 구역에
 *   %로 못박는다. 그래서 배경과 휠이 절대 따로 놀지 않는다.
 *   배경에서 잰 구역:  제목 11~30% · 무대 30~72% · 연단 72~80%
 */
type SpinRes = { prize: number; index: number; left: number; prizes: number[] };

const A = '/dev/royal/wheel';
// 링과 휠, 두 그림의 원 중심·지름을 재서 얻은 값. 자르지 않고 그대로 겹친다.
const WHEEL_W    = 75.71;       // 휠 이미지 폭 (컨테이너 폭 대비 %)
const WHEEL_LEFT = 12.05;       // 휠 왼쪽 (컨테이너 폭 대비 %)
const WHEEL_TOP  = 13.83;       // 휠 위쪽 (컨테이너 높이 대비 %)
const RING_CY    = 50.08;       // 링 구멍 중심의 세로 위치 (%)
// 당첨 패널(panel.webp) 안쪽 파란 상자 — 금색 테두리 안쪽을 실측한 값(%)
const BOX_L = 11.05, BOX_T = 61.90, BOX_W = 77.24, BOX_H = 14.71;

const SPIN_MS = 6800;
const EASE = 'cubic-bezier(.33,.02,.16,1)';   // 천천히 → 빠르게 → 천천히

const BG: React.CSSProperties = {
  // [2026-10-22] 상자를 그림과 같은 비율(1080:2340)로 잡아두면 cover 로도 잘릴 게 없다.
  // contain 으로 두면 상자보다 그림이 작아져, 상자 기준으로 놓인 휠과 어긋난다.
  background: `url(${A}/bg.webp) center / cover no-repeat`,
  backgroundColor: '#0A1136',
};
const SHADOW = '0 2px 6px rgba(0,0,0,.85)';

/**
 * 숫자 + "당첨" 을 패널 상자 한가운데에.
 *
 * 글자 크기를 CSS % 로 주면 안 된다 — 부모(span)의 높이가 내용으로 정해지는
 * 탓에 %가 무시되고 원본 120px 로 튀어나온다(실제로 그렇게 터졌다).
 * 상자 높이를 px 로 재서 그 비율로 직접 먹인다. 패널 그림이 늦게 떠서
 * 상자 크기가 나중에 정해지므로 ResizeObserver 로 따라간다.
 */
function PrizeLine({ value }: { value: number }) {
  const box = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 자릿수가 많으면(10000 등) 통째로 줄여서 상자 밖으로 안 나가게
  useLayoutEffect(() => {
    const rw = row.current?.scrollWidth || 0;
    setScale(size.w && rw > size.w ? size.w / rw : 1);
  }, [value, size.w, size.h]);

  const gh = Math.round(size.h * 0.62);    // 숫자 높이
  const wh = Math.round(size.h * 0.53);    // "당첨" 높이

  return (
    <div ref={box} style={{
      position: 'absolute',
      left: `${BOX_L}%`, top: `${BOX_T}%`, width: `${BOX_W}%`, height: `${BOX_H}%`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      <div ref={row} style={{
        display: 'flex', alignItems: 'center', gap: Math.round(gh * 0.3),
        whiteSpace: 'nowrap', transform: `scale(${scale})`, transformOrigin: 'center',
        opacity: gh ? 1 : 0,
      }}>
        {String(Math.round(Number(value))).split('').map((c, i) => (
          <img key={i} src={`${A}/n_${c}.webp`} alt={c}
               style={{ height: gh, display: 'block', marginRight: Math.round(gh * 0.04) }} />
        ))}
        <img src={`${A}/win.webp`} alt="당첨" style={{ height: wh, display: 'block' }} />
      </div>
    </div>
  );
}

/**
 * @param embed 탭 안에 그대로 박아 쓸 때 true. 전체화면 대신 그 자리를 채우고
 *              닫기 버튼을 숨긴다(탭 자체가 화면이라 닫을 곳이 없다).
 */
export function FreeWheel({ authHeaders, onClose, onDone, embed = false }: {
  authHeaders: () => Record<string, string>;
  onClose: () => void;
  onDone: () => void;
  embed?: boolean;
}) {
  const [state, setState] = useState<any>(null);
  const [n, setN] = useState(10);
  const [angle, setAngle] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'spin' | 'win'>('idle');
  const [res, setRes] = useState<SpinRes | null>(null);
  const [err, setErr] = useState('');
  const timers = useRef<any[]>([]);

  /**
   * [2026-10-22] 당첨 화면의 그림(폭죽·숫자·당첨)을 미리 받아둔다.
   * 안 그러면 돌린 직후 버튼만 덩그러니 뜨고 그림이 한 박자씩 늦게 들어와
   * 화면이 드드득 끊겨 보인다.
   */
  useEffect(() => {
    ['burst.webp', 'win.webp', 'bg.webp', ...Array.from({ length: 10 }, (_, i) => `n_${i}.webp`)]
      .forEach(f => { const im = new Image(); im.src = `${A}/${f}`; });
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const load = async () => {
    try {
      const j = await fetch('/api/game/royale/spin', { headers: authHeaders() }).then(r => r.json());
      if (j.success) { setState(j); if (j.prizes?.length) setN(j.prizes.length); }
    } catch { /* 화면은 떠야 한다 */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const start = async () => {
    if (phase !== 'idle') return;
    setErr('');
    try {
      const j: any = await fetch('/api/game/royale/spin', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      }).then(r => r.json());
      if (!j.success) throw new Error(j.error || '돌리지 못했습니다');

      const total = j.prizes?.length || n;
      if (j.prizes?.length) setN(total);
      const SEG = 360 / total;

      setPhase('spin');
      // 당첨 칸의 한가운데가 위쪽 바늘에 오도록. 여러 바퀴 돈 뒤 멈춘다.
      setAngle(a => Math.ceil(a / 360) * 360 + 360 * 7 - (j.index * SEG));

      timers.current.push(setTimeout(() => {
        setRes(j); setPhase('win');
        try { navigator.vibrate?.([60, 70, 150]); } catch { /* 막힌 기기 */ }
      }, SPIN_MS + 120));
    } catch (e: any) {
      setErr(e.message); setPhase('idle');
    }
  };

  const left = res ? res.left : (state?.left ?? 0);

  return (
    <div style={{
      ...(embed
        ? { position: 'relative', width: '100vw', marginLeft: 'calc(50% - 50vw)',
            height: 'calc(100dvh - 150px)', minHeight: 420 }
        : { position: 'fixed', inset: 0, zIndex: 200 }),
      color: '#fff',
      overflow: 'hidden',            // 스크롤 금지 — 배경과 휠이 따로 놀면 안 된다
      ...BG,
    }}>
      <style>{`
        @keyframes rw-pulse{ 0%,100%{transform:translate(-50%,-50%) scale(1)}
                             50%{transform:translate(-50%,-50%) scale(1.05)} }
        @keyframes rw-fall { 0%{transform:translateY(-14vh) rotate(0);opacity:1}
                             100%{transform:translateY(112vh) rotate(760deg);opacity:.85} }
        @keyframes rw-pop  { 0%{transform:scale(.55);opacity:0}
                             65%{transform:scale(1.07);opacity:1} 100%{transform:scale(1);opacity:1} }
        @keyframes rw-glowin{ 0%{transform:translate(-50%,-50%) scale(.4);opacity:0}
                              45%{opacity:1} 100%{transform:translate(-50%,-50%) scale(1.15);opacity:.55} }
        @keyframes rw-burst{ 0%{transform:scale(.3);opacity:0}
                             40%{opacity:1} 100%{transform:scale(1.3);opacity:0} }
      `}</style>

      {!embed && <button onClick={onClose} aria-label="닫기" style={{
        position: 'absolute', top: '1.2%', left: '2%', zIndex: 5,
        background: 'none', border: 0, color: '#fff', fontSize: 30,
        opacity: .9, padding: '2px 10px', cursor: 'pointer', textShadow: SHADOW,
      }}>‹</button>}

      {/* 제목은 배경 그림에 들어있다(11~30%). 그 아래 빈 자리에 남은 횟수. */}
      <div style={{
        position: 'absolute', top: '25.5%', left: 0, right: 0, textAlign: 'center',
      }}>
        <span style={{
          display: 'inline-block', padding: '5px 14px', borderRadius: 999,
          background: 'rgba(4,10,40,.62)', border: '1px solid rgba(255,212,71,.45)',
          fontSize: 14, fontWeight: 700, letterSpacing: '.2px', textShadow: SHADOW,
        }}>
          하루 {state?.perDay ?? 3}번 · 남은 횟수{' '}
          <b style={{ color: '#FFD447', fontSize: 17 }}>{left}</b>
        </span>
      </div>

      {/* 휠 — 배경의 빈 무대(30~72%) 한가운데. 링(고정) 안에 본체(회전). */}
      <div style={{
        position: 'absolute', left: '50%', top: '51%', transform: 'translate(-50%,-50%)',
        height: '41%', aspectRatio: '780 / 803', maxWidth: 'min(82%, 330px)',
      }}>
        {phase === 'win' && (
          <img src={`${A}/glow.webp`} alt="" style={{
            position: 'absolute', left: '50%', top: `${RING_CY}%`, width: '155%',
            transform: 'translate(-50%,-50%)',
            animation: 'rw-glowin 1.2s ease-out forwards', pointerEvents: 'none', zIndex: 0,
          }} />
        )}

        <img src={`${A}/wheel.webp`} alt="" style={{
          position: 'absolute',
          left: `${WHEEL_LEFT}%`, top: `${WHEEL_TOP}%`, width: `${WHEEL_W}%`,
          transform: `rotate(${angle}deg)`, transformOrigin: '50% 50%',
          transition: phase === 'spin' ? `transform ${SPIN_MS}ms ${EASE}` : 'none',
          zIndex: 1,
        }} />

        <img src={`${A}/ring.webp`} alt="" style={{
          position: 'relative', width: '100%', display: 'block', zIndex: 2, pointerEvents: 'none',
        }} />
      </div>

      {/* 연단(72~80%) 위에 확인 버튼 */}
      {phase === 'idle' && !res && (
        <button onClick={start} disabled={!state?.eligible} style={{
          position: 'absolute', left: '50%', top: '76.5%', transform: 'translate(-50%,-50%)',
          width: 'min(62%, 230px)', border: 0, background: 'none', padding: 0, cursor: 'pointer',
          opacity: state?.eligible ? 1 : .45,
          animation: state?.eligible ? 'rw-pulse 1.9s ease-in-out infinite' : 'none',
        }}>
          <img src={`${A}/btn_ok.webp`} alt="확인" style={{ width: '100%', display: 'block' }} />
        </button>
      )}

      {phase === 'spin' && (
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '76.5%', textAlign: 'center',
          fontSize: 16, opacity: .95, letterSpacing: 2, textShadow: SHADOW,
        }}>행운을 빕니다…</div>
      )}

      {phase === 'idle' && !res && (
        <div style={{
          position: 'absolute', left: '7%', right: '7%', top: '86%', textAlign: 'center',
          fontSize: 12, opacity: .9, lineHeight: 1.75, textShadow: SHADOW,
        }}>
          {state && !state.eligible
            ? `오늘 ${state.perDay}번을 모두 쓰셨습니다 · 내일 다시 돌릴 수 있습니다`
            : <>확인을 누르면 돌아갑니다<br />받은 금액은 <b>바로 지갑에 들어갑니다</b></>}
          {err && <div style={{ marginTop: 8, color: '#FCA5A5', fontSize: 13 }}>{err}</div>}
        </div>
      )}

      {/* 당첨 — 같은 배경 위에 어둡게 덮어서 딴 화면으로 튀지 않게 */}
      {phase === 'win' && res && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10, overflow: 'hidden',
          background: `radial-gradient(95% 55% at 50% 32%, rgba(14,30,92,.86), rgba(4,7,20,.95)),
                       url(${A}/bg.webp) center / cover no-repeat`,
          backgroundColor: '#070B22',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          {Array.from({ length: 30 }, (_, i) => (
            <span key={i} style={{
              position: 'absolute', top: 0, left: `${(i * 31) % 100}%`,
              width: 8, height: 14, borderRadius: 1,
              background: ['#FFD447', '#45D6FF', '#FFFFFF', '#3B6BE8', '#FFB020'][i % 5],
              animation: `rw-fall ${2.3 + (i % 5) * .5}s linear ${(i % 8) * .2}s infinite`,
            }} />
          ))}

          <img src={`${A}/burst.webp`} alt="" style={{
            position: 'absolute', width: 'min(125vw, 540px)',
            animation: 'rw-burst 1.6s ease-out', pointerEvents: 'none',
          }} />

          {/* 46vh 상한 — 낮은 화면에서 패널+버튼이 잘리지 않게 */}
          <div style={{ position: 'relative', width: 'min(88vw, 340px, 46vh)',
                        animation: 'rw-pop .55s ease-out' }}>
            <img src={`${A}/panel.webp`} alt="축하합니다"
                 style={{ width: '100%', display: 'block' }} />
            <PrizeLine value={res.prize} />
          </div>

          <div style={{ fontSize: 12, opacity: .85, marginTop: 10, textShadow: SHADOW }}>
            획득한 보상은 즉시 지급됩니다 · 남은 횟수 {res.left}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
            <button onClick={() => { onDone(); if (!embed) onClose(); else { setPhase('idle'); setRes(null); load(); } }} style={{
              border: 0, background: 'none', padding: 0, cursor: 'pointer',
              width: 'min(38vw, 150px)',
            }}>
              <img src={`${A}/btn_ok.webp`} alt="확인" style={{ width: '100%', display: 'block' }} />
            </button>
            {res.left > 0 && (
              <button onClick={() => { setRes(null); setPhase('idle'); load(); }} style={{
                border: 0, background: 'none', padding: 0, cursor: 'pointer',
                position: 'relative', width: 'min(38vw, 150px)',
              }}>
                <img src={`${A}/btn_blue.webp`} alt="" style={{ width: '100%', display: 'block' }} />
                <span style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 16, fontWeight: 900,
                }}>한 번 더</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
